import { Theme } from '@wds/styles';
import React, { ChangeEvent, FormEvent, FormEventHandler, useContext, useEffect, useReducer, useState } from 'react';
import { createUseStyles } from 'react-jss';

import { clientErrorHandler, StaticContext } from '@sm/webassets';
import { Input } from '@wds/input';
import { Box } from '@wds/box';

// local augmentation
declare global {
  export namespace JSX {
    interface IntrinsicElements {
      'wds-strong-password': unknown;
    }
  }
}

const useStyles = createUseStyles((theme: Theme) => ({
  loadingContainer: {
    // Give loading layout the same dimension as final layout to avoid jiggle
    marginBottom: '55px',
  },
  strongPasswordContainer: {
    // We can't directly style webcomponent internals, so offset container upward to prevent
    // unwanted empty space above the input.
    marginTop: -theme.spacing[4],

    // Style the strong-password input to resemble WDS Input
    '& wds-strong-password::part(password)': {
      borderRadius: theme.radius.small,
      fontFamily: theme.type.fontFamily.base,
      height: '40px',
      padding: [0, theme.spacing[3]],

      '&:hover': {
        borderColor: theme.palette.border.hover,
        boxShadow: theme.shadow.sky,
        transition: ['border-color 100ms', 'box-shawdow 100ms'],
      },
    },
    '& wds-strong-password::part(status)': {
      fontFamily: theme.type.fontFamily.base,
      fontSize: theme.type.fontSize.bodySm,
    },
  },
}));

type StrongPasswordProps = {
  id: string;
  name?: string;
  placeholder?: string;
  required?: boolean;
  onInput?: FormEventHandler<HTMLInputElement>;
  onValid?(valid: boolean): void;
};

type StrongPasswordInputEvent = InputEvent & {
  nativeEvent: InputEvent & {
    password: string;
    valid: boolean;
  };
  valid: boolean;
};

/**
 * StrongPassword: Password input with strength meter and feedback on breached or forbidden passwords
 * @param id used to identify the Input Component
 * @param name used as the name of the Input Component
 * @param onInput event handler to set the update state in parent Component
 * @param onValid function called when input is entered and given boolean indicating when the input is valid
 * @param placeholder the value set in the password field before a value is set
 * @param required a boolean indicating weather the field must be filled to complete the form
 */
export default function StrongPassword({
  id,
  name,
  onInput = () => {},
  onValid = () => {},
  placeholder,
  required,
}: StrongPasswordProps): React.ReactElement {
  const {
    environment: { languageCode },
    'client-config': { zephyrHost },
  } = useContext(StaticContext);

  const zephyrWebsocketHost = zephyrHost?.replace('https://', 'wss://');
  const [value, setValue] = useState('');
  const [isLoaded, setIsLoaded] = useReducer(() => true, false); // one way only
  const { loadingContainer, strongPasswordContainer } = useStyles();

  useEffect(() => {
    // need to do dynamic import, but just once
    if (!isLoaded && typeof window !== 'undefined') {
      import('@sm/strong-password')
        .then(() => setIsLoaded())
        .catch(e => clientErrorHandler.logError(e, 'Failed to load @sm/strong-password'));
    }
  }, [isLoaded]);

  // Progressive enhancement: render a regular WDS input while loading the library. When it
  // loads, swap it for the real wds-strong-password component
  if (!isLoaded) {
    const onWdsInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
      setValue(e.target.value);
      onInput(e);
    };
    return (
      <Box className={loadingContainer}>
        <Input
          id={id}
          type="password"
          placeholder={placeholder}
          required={required}
          name={name}
          stretched
          value={value}
          onChange={onWdsInputChange}
        />
      </Box>
    );
  }

  return (
    <Box className={strongPasswordContainer}>
      {/* The HTML form won't see strong-password because it lives in the shadow DOM.
          Use a hidden field to work around this.
      */}
      <input type="hidden" name={name} value={value} />

      <wds-strong-password
        id={id}
        host={zephyrWebsocketHost}
        placeholder={placeholder}
        locale={languageCode}
        required={required ? true : undefined}
        value={value}
        onInput={(e: StrongPasswordInputEvent) => {
          const newValue = e.nativeEvent.password;
          setValue(newValue);

          onInput({
            target: {
              value: newValue,
            },
          } as unknown as FormEvent<HTMLInputElement>);

          onValid(e.nativeEvent.valid);
        }}
      />
    </Box>
  );
}
